<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useResumeRefresh } from "../useResumeRefresh";
import { api, type PlanDraft, type PlanPayload } from "../api";
import type { AgentWeekInput } from "../shared";
import PlanBuilder from "./PlanBuilder.vue";

const emit = defineEmits<{ error: [message: string]; toast: [message: string]; workoutStarted: [] }>();
const props = defineProps<{ active: boolean; refreshKey: number }>();
const plan = ref<PlanPayload | null>(null);
const drafts = ref<PlanDraft[]>([]);
const loading = ref(true);
const copied = ref(false);
const editing = ref<{ week: AgentWeekInput; draftId?: string; draftRevision?: number; weekRevision?: number } | null>(null);
const startingDayId = ref<string | null>(null);
const selectedWeekStart = ref<string | null>(null);
let loadRequest = 0;
let loadPending = false;
const orderedWeeks = computed(() => [...(plan.value?.weeks ?? [])].sort((a, b) => a.week_start.localeCompare(b.week_start)));
const activeWeek = computed(() => {
  if (!orderedWeeks.value.length) return null;
  return orderedWeeks.value.find((week) => week.week_start === selectedWeekStart.value)
    ?? orderedWeeks.value.reduce((closest, week) => Math.abs(new Date(`${week.week_start}T12:00:00`).getTime() - localWeekStart().getTime()) < Math.abs(new Date(`${closest.week_start}T12:00:00`).getTime() - localWeekStart().getTime()) ? week : closest);
});
const activeDays = computed(() => activeWeek.value ? plan.value?.days.filter((day) => day.routine_week_id === activeWeek.value?.id) ?? [] : []);
const activeSession = computed(() => plan.value?.active_session ?? null);
const activeSessionDayId = computed(() => activeSession.value?.routine_day_id ?? plan.value?.days.find((day) => day.session_status === "active")?.id ?? null);
function blocks(dayId: string) { return plan.value?.blocks.filter((block) => block.routine_day_id === dayId) ?? []; }
function displayDate(value: string) { return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(new Date(`${value}T12:00:00`)); }
function isoDate(value: Date): string { return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`; }

function localWeekStart(): Date {
  const value = new Date();
  value.setHours(12, 0, 0, 0);
  value.setDate(value.getDate() - ((value.getDay() + 6) % 7));
  return value;
}

function localWeekStartIso(): string { return isoDate(localWeekStart()); }

function nextMonday(): string {
  const value = new Date();
  value.setHours(12, 0, 0, 0);
  value.setDate(value.getDate() + ((8 - value.getDay()) % 7 || 7));
  return isoDate(value);
}

function chooseDefaultWeek() {
  if (!orderedWeeks.value.length) { selectedWeekStart.value = null; return; }
  if (selectedWeekStart.value && orderedWeeks.value.some((week) => week.week_start === selectedWeekStart.value)) return;
  const current = localWeekStartIso();
  selectedWeekStart.value = orderedWeeks.value.find((week) => week.week_start === current)?.week_start
    ?? orderedWeeks.value.reduce((closest, week) => Math.abs(new Date(`${week.week_start}T12:00:00`).getTime() - localWeekStart().getTime()) < Math.abs(new Date(`${closest.week_start}T12:00:00`).getTime() - localWeekStart().getTime()) ? week : closest).week_start;
}

function moveWeek(direction: -1 | 1) {
  const index = orderedWeeks.value.findIndex((week) => week.id === activeWeek.value?.id);
  const next = orderedWeeks.value[index + direction];
  if (next) selectedWeekStart.value = next.week_start;
}

function blankWeek(): AgentWeekInput {
  const monday = nextMonday();
  return {
    week_start: monday, title: "Next training week", focus: "", coach_notes: "", warmup: [],
    days: [{ day_date: monday, title: "Workout 1", focus: "", estimated_minutes: 50, blocks: [{
      label: "A", kind: "superset", rounds: 3, rest_seconds: 90,
      exercises: [
        { name: "", target_sets: 3, rep_min: 8, rep_max: 10, target_rir: 2 },
        { name: "", target_sets: 3, rep_min: 8, rep_max: 10, target_rir: 2 }
      ]
    }] }]
  };
}

async function load(background = false) {
  if (background && loadPending) return;
  const requestId = ++loadRequest;
  loadPending = true;
  loading.value = plan.value === null;
  try {
    const nextPlan = await api.plan();
    if (requestId !== loadRequest) return;
    plan.value = nextPlan;
    chooseDefaultWeek();
    try {
      const nextDrafts = await api.drafts();
      if (requestId === loadRequest) drafts.value = nextDrafts.drafts;
    }
    catch (caught) { if (requestId === loadRequest) emit("error", caught instanceof Error ? caught.message : "Could not load drafts"); }
  } catch (caught) { if (requestId === loadRequest) emit("error", caught instanceof Error ? caught.message : "Could not load plan"); }
  finally { if (requestId === loadRequest) { loading.value = false; loadPending = false; } }
}

async function duplicateCurrent() {
  if (!activeWeek.value) return;
  try {
    const fetched = await api.week(activeWeek.value.id);
    const duplicate = structuredClone(fetched.week);
    delete duplicate.expected_revision;
    const oldStart = new Date(`${duplicate.week_start}T12:00:00`);
    const newStart = new Date(oldStart); newStart.setDate(newStart.getDate() + 7);
    const delta = newStart.getTime() - oldStart.getTime();
    duplicate.week_start = isoDate(newStart);
    duplicate.title = `${duplicate.title} · next week`;
    duplicate.days.forEach((day) => { const date = new Date(`${day.day_date}T12:00:00`); day.day_date = isoDate(new Date(date.getTime() + delta)); });
    editing.value = { week: duplicate, weekRevision: plan.value?.weeks.find((week) => week.week_start === duplicate.week_start)?.revision };
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not duplicate week"); }
}

async function saved() { emit("toast", "Draft saved"); await load(); }
async function published() { editing.value = null; emit("toast", "Workout week published"); await load(); }

async function startDay(day: PlanPayload["days"][number]) {
  startingDayId.value = day.id;
  try {
    await api.startSession(day.id);
    emit("toast", day.session_status === "active" ? "Workout resumed" : `${day.title} started`);
    emit("workoutStarted");
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not start workout"); }
  finally { startingDayId.value = null; }
}

function dayActionLabel(day: PlanPayload["days"][number]): string {
  if (startingDayId.value === day.id) return day.session_status === "active" ? "Resuming…" : "Starting…";
  if (day.session_status === "active" || activeSessionDayId.value === day.id) return "Resume workout";
  if (activeSession.value) return "Finish active workout first";
  if (day.session_status === "completed") return "Repeat workout";
  return "Start workout";
}

async function copyAgentPrompt() {
  await navigator.clipboard.writeText(`Use my Superset Workout Agent API at ${location.origin}/api/openapi.json. Authenticate with the personal token I provide. First call getPlanningContext and follow the returned coach_contract. Act as the training expert: treat my comments and preferences as evidence, preserve each movement's purpose, explain your recommendation, and push back respectfully when warranted. Then plan and replace my next workout week. Optimize for two-exercise supersets, preserve exercise names when useful for continuity, put session preparation in the warmup checklist, use duration_seconds for time-based work (never seconds as reps), and include rep ranges, target RIR, progression notes, short cues, and technique video URLs.`);
  copied.value = true;
  window.setTimeout(() => { copied.value = false; }, 1800);
}

function resumeActive() { emit("workoutStarted"); }

watch(() => [props.active, props.refreshKey], ([active]) => { if (active) void load(); }, { immediate: true });
useResumeRefresh(() => props.active, () => load(true));
</script>

<template>
  <section class="view plan-view">
    <PlanBuilder v-if="editing" :initial="editing.week" :draft-id="editing.draftId" :draft-revision="editing.draftRevision" :week-revision="editing.weekRevision" @cancel="editing = null" @saved="saved" @published="published" @error="emit('error', $event)" />
    <template v-else>
      <header class="view-header"><div><p class="eyebrow">Programming</p><h1>{{ activeWeek ? `Week of ${displayDate(activeWeek.week_start)}` : "Plan" }}</h1></div><div class="week-nav"><button class="icon-button" :disabled="!activeWeek || orderedWeeks.findIndex((week) => week.id === activeWeek?.id) <= 0" aria-label="Previous week" @click="moveWeek(-1)">←</button><button class="icon-button" :disabled="!activeWeek || orderedWeeks.findIndex((week) => week.id === activeWeek?.id) >= orderedWeeks.length - 1" aria-label="Next week" @click="moveWeek(1)">→</button><a class="icon-button" href="/api/openapi.json" target="_blank" aria-label="Open agent API schema">{ }</a></div></header>
      <div class="plan-actions"><button class="button primary" @click="editing = { week: blankWeek() }">＋ Build a week</button><button v-if="activeWeek" class="button ghost" @click="duplicateCurrent">Duplicate current</button></div>
      <div v-if="loading" class="skeleton-stack"><i></i><i></i><i></i></div>
      <template v-else-if="activeWeek">
        <div v-if="activeSession && !activeDays.some((day) => day.id === activeSession?.routine_day_id)" class="active-session-banner"><div><p class="eyebrow">Workout in progress</p><b>{{ activeSession.title }}</b><small>{{ activeSession.routine_day_id ? "From another week" : "Needs recovery" }}</small></div><button class="button primary" @click="resumeActive">Resume workout</button></div>
        <section class="week-card">
          <p class="eyebrow">Week of {{ displayDate(activeWeek.week_start) }} · {{ activeWeek.source }}</p>
          <h2>{{ activeWeek.title }}</h2><p>{{ activeWeek.focus }}</p>
          <div v-if="activeWeek.warmup.length" class="warmup-note plan-warmup"><span>WARM-UP</span><ul><li v-for="step in activeWeek.warmup" :key="step">{{ step }}</li></ul></div>
          <div v-if="activeWeek.coach_notes" class="week-note">{{ activeWeek.coach_notes }}</div>
        </section>
        <div class="days-list">
          <article v-for="(day, index) in activeDays" :key="day.id" class="day-card">
            <header><span>{{ String(index + 1).padStart(2, "0") }}</span><div><p>{{ displayDate(day.day_date) }}</p><h3>{{ day.title }}</h3></div><b>{{ day.estimated_minutes }}m</b></header>
            <p>{{ day.focus }}</p>
            <div v-for="block in blocks(day.id)" :key="block.id" class="plan-block"><b>{{ block.label }}</b><span>{{ block.exercise_names }}</span><small>{{ block.rounds }} rounds · {{ block.rest_seconds }}s</small></div>
            <button
              class="button day-start"
              :class="day.session_status === 'active' ? 'primary' : 'secondary'"
              :disabled="Boolean(activeSession && activeSessionDayId !== day.id) || Boolean(startingDayId)"
              @click="startDay(day)"
            >
              {{ dayActionLabel(day) }}
            </button>
          </article>
        </div>
      </template>
      <section v-else-if="!loading" class="empty-plan"><h2>No published week yet</h2><p>Start from a clean builder or let your planning agent create one.</p></section>

      <section v-if="drafts.some((draft) => draft.status === 'draft')" class="drafts-card">
        <header><div><p class="eyebrow">Workbench</p><h2>Saved drafts</h2></div><span>{{ drafts.filter((draft) => draft.status === 'draft').length }}</span></header>
        <button v-for="draft in drafts.filter((item) => item.status === 'draft')" :key="draft.id" @click="editing = { week: draft.payload, draftId: draft.id, draftRevision: draft.revision, weekRevision: plan?.weeks.find((week) => week.week_start === draft.week_start)?.revision ?? 0 }">
          <div><b>{{ draft.title }}</b><small>Week of {{ displayDate(draft.week_start) }} · edited {{ new Date(draft.updated_at).toLocaleDateString() }}</small></div><span>Continue →</span>
        </button>
      </section>

      <section class="agent-card">
        <div class="agent-orbit" aria-hidden="true"><span>✦</span></div>
        <div><p class="eyebrow">Agent planning bridge</p><h2>Let Codex write the week</h2><p>Give Codex a revocable personal token. It can read completed performance and publish a week without sharing your browser login.</p></div>
        <button class="button secondary" @click="copyAgentPrompt">{{ copied ? "Prompt copied" : "Copy setup prompt" }}</button>
        <div class="endpoint"><span>SCHEMA</span><code>/api/openapi.json</code></div>
        <div class="endpoint"><span>CONTEXT</span><code>/api/agent/context</code></div>
        <p class="agent-settings-note">Create and revoke agent credentials in Settings.</p>
      </section>
    </template>
  </section>
</template>
